import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormFooter, FormSection } from '@atlaskit/form';
import {
  ErrorComponents,
  InfoPanel,
  InvoiceGroupSelectItem,
  orderSuccessNotification,
  RequiredFieldMessage,
  useFormSubmit,
  useNotification,
  useSidebar,
  SelectPromotionSelectItem,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { Promotion } from '@css-xp/graphtypes';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { OMSMetadata } from '@atlassian/cssxp-types-entitlement';
import { PromotionInstance, UpdateOrderRequest } from '@atlassian/cssxp-typescommon';
import React from 'react';
import { EntitlementChangePlanForm } from './change-plan-form-field';
import get from 'lodash/get';
import { promotionAddModifier } from '../../utils/promotion-utils';
import { withApollo } from '@css-xp/base/graphql-provider';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
export interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

interface SelectItem {
  label: string;
  value: string;
}

interface Props {
  entitlement: EntitlementDetails;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export interface FormData {
  offeringSelectedItem?: OfferingSelectItem;
  billingFrequency?: SelectItem;
  supportTicketNumber?: string | null;
  invoiceGroupId?: InvoiceGroupSelectItem;
  promotionId?: SelectPromotionSelectItem;
  pricingPlan?: string;
  skipTrial?: boolean;
  offeringQuantity?: string;
  handNote?: string | null;
  customReason?: {
    value: string | undefined | null;
    label: string | undefined | null;
  };
  customValue?: number;
  customDuration?: {
    value: string | undefined | null;
    label: string | undefined | null;
  };
  customIterations?: number;
}

const constructRequestData = (
  entitlement: EntitlementDetails,
  formData: FormData,
  {
    isViewPromotionDetailsEnabled,
    canChangePlanAndPromotion,
  }: {
    isViewPromotionDetailsEnabled: boolean;
    canChangePlanAndPromotion: boolean;
  }
) => {
  const requestData: UpdateOrderRequest = {
    offeringId: formData.offeringSelectedItem?.value.id ?? '',
    billingFrequency: formData.billingFrequency?.value ?? 'MONTHLY',
    invoiceGroupId: formData.invoiceGroupId?.value ?? '',
    metadata: {
      [OMSMetadata.SUPPORT_TICKET_NUMBER]: formData.supportTicketNumber ?? null,
    },
    entitlementId: entitlement.id,
    productId: entitlement.productId ?? '',
  };

  if (formData.skipTrial) {
    requestData.skipTrial = true;
  }

  if (!canChangePlanAndPromotion && formData?.pricingPlan) {
    requestData.pricingPlanType = formData?.pricingPlan;
  }

  if (isViewPromotionDetailsEnabled) {
    requestData.promotionsToAdd = formData?.promotionId?.promotion?.id ? [formData.promotionId.promotion.id] : [];

    const promotionsToRemove =
      // @ts-ignore
      get(entitlement, 'promotions', [])?.map((promotion: Promotion) => promotion?.id as string) ||
      get(entitlement, 'scheduledChanges.promotions', [])?.map((promotion: Promotion) => promotion?.id as string) ||
      [];
    requestData.promotionsToRemove = promotionsToRemove;

    if (formData?.promotionId?.promotion?.id) {
      requestData.addPromotions = promotionAddModifier({
        promotionSelectItem: formData.promotionId,
        customDuration: formData.customDuration,
        customReason: formData.customReason,
        customValue: formData.customValue,
        customIterations: formData.customIterations,
      });
    } else {
      const removePromotionInstance: PromotionInstance[] = [];
      promotionsToRemove.forEach((promo: string) => {
        const promotionInstance: PromotionInstance = {
          promotionDefinition: {
            promotionId: promo,
          },
        };
        removePromotionInstance.push(promotionInstance);
        requestData.removePromotions = removePromotionInstance;
      });
    }
  }
  return requestData;
};
let startDate: number;

const setStartDate = () => {
  if (!startDate) {
    startDate = new Date().getTime();
  }
};

export const EntitlementChangePlanPane = withApollo(({ entitlement, refetch, transactionAccountContext }: Props) => {
  setStartDate();
  const { error: formError, isProcessing, submitForm, complete, data } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      refetch();
      analytics.sendTrackEvent({
        attributes: {
          ticketNumber: data.supportTicketNumber,
        },
        action: AnalyticsTrackAction.UPDATED,
        actionSubject: AnalyticsTrackSubject.CHANGE_PRICING_PLAN,
      });
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const isViewPromotionDetailsEnabled = true; //TO:DO
  const canChangePlanAndPromotion = false; //useSelector(getFeatureFlag(ENABLE_CHANGE_PRICING_PLAN_PROMOTION_SIDEBAR))
  const isUpdateUnitsFeatureEnabled = true; //useSelector(getFeatureFlag(ENABLE_STATUSPAGE_UPDATE_UNITS));

  const { getTransactionAccount, data: transactionAccountData } = transactionAccountContext;

  const { closeSidebar } = useSidebar();

  const transactionAccount = getTransactionAccount(transactionAccountData);

  // const notificationMessageHeader = canChangePlanAndPromotion ? 'UPDATE_PLAN' : 'MANAGE_SUBSCRIPTION';
  const notificationMessageHeader = 'MANAGE_SUBSCRIPTION';

  const handleFormSubmit = (formData: FormData) => {
    const requestData = constructRequestData(entitlement, formData, {
      isViewPromotionDetailsEnabled,
      canChangePlanAndPromotion,
    });

    // if (isUpdateUnitsFeatureEnabled) {
    requestData.units = formData.offeringQuantity;
    // }
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const transitionTimeError =
      data.transitionTime === 'unknown'
        ? ' - We were unable to determine if the successfully submitted order is immediate or scheduled.'
        : undefined;

    const notificationOptions = orderSuccessNotification(
      notificationMessageHeader,
      data.transitionTime,
      transitionTimeError
    );
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  return (
    <div className="entitlement-change-plan-form" style={{ maxWidth: '600px' }}>
      <h3>Manage subscription</h3>
      <RequiredFieldMessage />
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to change the plan for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <EntitlementChangePlanForm
              entitlement={entitlement}
              transactionAccount={transactionAccount}
              withPromotionFlag={true}
              pricingPlanFlag={!canChangePlanAndPromotion}
            />
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} testId="change-plan">
                  Change plan
                </LoadingButton>
                <Button appearance="subtle" testId="cancel" onClick={() => closeSidebar()}>
                  Cancel
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
});
